import type { ElementRef, QueryList } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChildren,
  ViewContainerRef,
  output,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import type { Company } from '@recruitee/company-types';
import { GdprConfigurationSelectors } from '@recruitee/gdpr-data';
import type { GdprConfiguration } from '@recruitee/gdpr-types';
import { GdprRequestKind } from '@recruitee/gdpr-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  DialogService,
  DialogWidth,
  DropdownItemComponent,
  HeadingComponent,
  IconComponent,
  KeyCode,
  RtStatefulComponent,
} from '@recruitee/user-interface';

import { RedactorInsetsGdprService } from './redactor-insets-gdpr.service';
import { RedactorInsertsDialogComponent } from '../../../wysiwyg-extensions/link/redactor-inserts-dialog/redactor-inserts-dialog.component';
import type { WysiwygLink } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { WysiwygLinkType } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsDropdownHeaderComponent } from '../redactor-inserts-dropdown-header/redactor-inserts-dropdown-header.component';

type DialogOpts = {
  label: string;
  link: string;
  period?: number;
  openInNewTab: boolean;
  type: WysiwygLinkType;
};

type State = {
  selectedIndex: number | null;
};

@Component({
  selector: 'rt-redactor-inserts-gdpr',
  templateUrl: './redactor-inserts-gdpr.component.html',
  styleUrls: ['./redactor-inserts-gdpr.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RedactorInsertsDropdownHeaderComponent,
    DropdownItemComponent,
    IconComponent,
    HeadingComponent,
    TranslatePipe,
  ],
})
export class RedactorInsertsGdprComponent extends RtStatefulComponent<State> {
  public readonly GdprRequestKind = GdprRequestKind;

  public readonly GDPR_REQUEST_KIND_TO_WYSIWYG_LINK_TYPE_MAP = {
    [GdprRequestKind.Request]: WysiwygLinkType.GdprConsentRequest,
    [GdprRequestKind.Removal]: WysiwygLinkType.GdprRemove,
    [GdprRequestKind.Correction]: WysiwygLinkType.GdprChange,
  };

  public readonly GDPR_REQUEST_KIND_HEADING_TRANSLATION_MAP = {
    [GdprRequestKind.Request]: this.i18n.translate(
      'app.gdpr.inserts_dialog.consent_request.heading',
    ),
    [GdprRequestKind.Removal]: this.i18n.translate('app.gdpr.inserts_dialog.removal.heading'),
    [GdprRequestKind.Correction]: this.i18n.translate('app.gdpr.inserts_dialog.change.heading'),
  };

  @Input() public hasBackAction: boolean = true;
  @Input() public isConsentRequestShow: boolean = true;

  public back = output<void>();
  public settings = output<string>();
  public closeDropdown = output<void>();
  public insertLink = output<WysiwygLink>();

  @ViewChildren('container') private containerList: QueryList<ElementRef>;

  private ngOnDestroy$ = new Subject<void>();
  private gdprConfiguration: GdprConfiguration;
  private currentCompany: Company;

  constructor(
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    private store: Store,
    private i18n: I18nService,
    private redactorInsertsGdprService: RedactorInsetsGdprService,

    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({ selectedIndex: null });
  }

  public ngOnInit() {
    this.store
      .select(GdprConfigurationSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(gdprConfiguration => {
        this.gdprConfiguration = gdprConfiguration;
        this.cdRef.detectChanges();
      });

    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentCompany => (this.currentCompany = currentCompany));

    if (this.containerList.first) {
      this.containerList.first.nativeElement.focus();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public openModal(requestKind: GdprRequestKind): void {
    const wysiwygLinkType = this.GDPR_REQUEST_KIND_TO_WYSIWYG_LINK_TYPE_MAP[requestKind];
    const heading = this.GDPR_REQUEST_KIND_HEADING_TRANSLATION_MAP[requestKind];
    const dialog = this.dialogService.openCustom(
      RedactorInsertsDialogComponent,
      { width: DialogWidth.Small },
      {
        type: wysiwygLinkType,
        heading,
        link: `[gdpr_request_${requestKind}_url]`,
        previewLink: this.redactorInsertsGdprService.createLink(requestKind),
        label: '',
        isEdit: false,
        expirationDays: this.gdprConfiguration.expirationDays,
      },
      this.vcRef,
    );
    dialog.afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(opts => {
      if (opts) {
        this.insertLink.emit(this.modalOptsToConsentLink(opts));
      }
      this.closeDropdown.emit();
    });
  }

  public openConsentRequestModal() {
    const link = this.redactorInsertsGdprService.createLink();
    const dialog = this.dialogService.openCustom(
      RedactorInsertsDialogComponent,
      { width: DialogWidth.Small },
      {
        type: WysiwygLinkType.GdprConsentRequest,
        heading: this.GDPR_REQUEST_KIND_HEADING_TRANSLATION_MAP[GdprRequestKind.Request],
        link,
        label: '',
        isEdit: false,
        expirationDays: this.gdprConfiguration.expirationDays,
      },
      this.vcRef,
    );
    dialog.afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(opts => {
      if (opts) {
        this.insertLink.emit(this.modalOptsToConsentLink(opts));
      }
      this.closeDropdown.emit();
    });
  }

  public goBack(): void {
    this.back.emit();
  }
  public goToSettings(url: string): void {
    this.settings.emit(url);
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    switch (key) {
      case KeyCode.Enter: {
        event.preventDefault();
        if (this.state.selectedIndex === null) {
          return;
        } else {
          this.state.selectedIndex === 0
            ? this.openModal(GdprRequestKind.Removal)
            : this.openModal(GdprRequestKind.Correction);
        }
        break;
      }

      case KeyCode.Backspace:
        if (this.hasBackAction) {
          event.preventDefault();
          this.back.emit();
        }
        break;

      case KeyCode.UpArrow: {
        event.preventDefault();
        if (this.state.selectedIndex === 0 || this.state.selectedIndex === null) {
          this.setState({ ...this.state, selectedIndex: 1 });
        } else {
          this.setState({ ...this.state, selectedIndex: this.state.selectedIndex - 1 });
        }
        break;
      }

      case KeyCode.DownArrow: {
        event.preventDefault();
        if (this.state.selectedIndex === 2 || this.state.selectedIndex === null) {
          this.setState({ ...this.state, selectedIndex: 0 });
        } else {
          this.setState({ ...this.state, selectedIndex: this.state.selectedIndex + 1 });
        }
        break;
      }

      default:
        return;
    }
  }

  private modalOptsToConsentLink(opts: DialogOpts): WysiwygLink {
    return {
      type: opts.type,
      id: opts?.period?.toString(),
      url: opts.link,
      label: opts.label || opts.link,
      target: opts.openInNewTab ? 'blank' : 'self',
    };
  }
}
